import { vnDateTimeToIso } from './attendance';

/**
 * DEC-760 – Trạng thái ngày phòng Vận Tải trên bảng công (anh Tiến 02/11/2026: "Phòng Vận Tải: làm Trạng thái duyệt:
 * Xuyên đêm, về trễ, hỗ trợ. Ngay trên bản chấm công").
 * - Mỗi NV mỗi ngày có tối đa 1 dòng cho mỗi loại; người quản lý chấm công khu vực (điểm chấm công, DEC-618) → TP → GĐ duyệt ngay trên ô.
 * - Người duyệt tự đánh dấu = đã duyệt luôn; người sửa công (HR) đánh dấu = chờ duyệt. Chưa duyệt / bị bác thì không tính.
 * - Chỉ ghi nhận + đếm ở cột tổng + xuất Excel; tiền tài xế vẫn theo bảng tiền tài xế (hỗ trợ đêm, qua đêm tính từ GPS / chuyến).
 */
export const DAY_MARK_KINDS = ['overnight', 'late_return', 'support'] as const;
export type DayMarkKind = (typeof DAY_MARK_KINDS)[number];
export const DAY_MARK_KIND_LABELS: Record<DayMarkKind, string> = {
  overnight: 'Xuyên đêm',
  late_return: 'Về trễ',
  support: 'Hỗ trợ',
};
/** Ký hiệu 1 chữ trên ô bảng công */
export const DAY_MARK_SYMBOLS: Record<DayMarkKind, string> = { overnight: 'Đ', late_return: 'T', support: 'H' };

export const DAY_MARK_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type DayMarkStatus = (typeof DAY_MARK_STATUSES)[number];
export const DAY_MARK_STATUS_LABELS: Record<DayMarkStatus, string> = {
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Không duyệt',
};

/** Giờ ra (giờ VN) từ mốc này trở đi thì gợi ý "Về trễ" */
export const LATE_RETURN_FROM = '21:00';

/**
 * Gợi ý loại theo giờ vào / ra (giờ VN 'HH:MM', ra có thể sang ngày hôm sau): ra sau 0h hoặc làm ≥ 18 giờ → Xuyên đêm;
 * ra từ 21:00 → Về trễ. Chỉ là gợi ý chọn sẵn, người duyệt quyết.
 */
export function suggestDayMark(checkIn: string | null, checkOut: string | null, nextDay = false): DayMarkKind | null {
  if (!checkOut) return null;
  const toMin = (t: string) => Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
  const out = toMin(checkOut) + (nextDay ? 1440 : 0);
  const inn = checkIn ? toMin(checkIn) : null;
  if (out >= 1440 || (inn !== null && out - inn >= 18 * 60)) return 'overnight';
  if (out >= toMin(LATE_RETURN_FROM)) return 'late_return';
  return null;
}

/** Đếm số ngày đã duyệt theo loại */
export function countApprovedMarks(marks: { kind: DayMarkKind; status: DayMarkStatus }[]): Record<DayMarkKind, number> {
  const c: Record<DayMarkKind, number> = { overnight: 0, late_return: 0, support: 0 };
  for (const m of marks) if (m.status === 'approved') c[m.kind] += 1;
  return c;
}

/** Loại trạng thái ngày có ô "Giờ về bãi" (DEC-767) */
export const DAY_MARK_RETURN_KINDS: readonly DayMarkKind[] = ['overnight', 'late_return'];
/** Giờ về trước mốc này (giờ VN) hiểu là sáng ngày hôm sau (cùng mốc ca tài xế tối đa tới 12:00 hôm sau) */
export const DAY_MARK_NEXT_DAY_BEFORE = '12:00';

/**
 * DEC-767 – mốc giờ về bãi: 'HH:MM' giờ VN của ngày công; trước 12:00 là sáng hôm sau (VD 23:08 cùng ngày, 02:30 hôm sau).
 * Trả về ISO UTC.
 */
export function dayMarkReturnIso(workDate: string, hhmm: string): string {
  const base = vnDateTimeToIso(workDate, hhmm);
  if (hhmm >= DAY_MARK_NEXT_DAY_BEFORE) return base;
  return new Date(Date.parse(base) + 86_400_000).toISOString();
}
